import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Truck, HandHeart, Ruler, Hammer, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Reveal, SectionHeading, EstimateBanner, HomeServicesBar } from '../components/shared';
import { heroSlides, categories, steps, reviews, business } from '../mock/mock';

const stepIcons = [Truck, HandHeart, Ruler, Hammer];

function Hero() {
  const [i, setI] = useState(0);
  const next = useCallback(() => setI((p) => (p + 1) % heroSlides.length), []);
  const prev = () => setI((p) => (p - 1 + heroSlides.length) % heroSlides.length);
  useEffect(() => {
    const t = setInterval(next, 6500);
    return () => clearInterval(t);
  }, [next]);
  const slide = heroSlides[i];
  return (
    <section className="relative h-[92vh] min-h-[560px] max-h-[860px] overflow-hidden">
      {heroSlides.map((s, idx) => (
        <div key={s.id} className={`absolute inset-0 transition-opacity duration-[1200ms] ${idx === i ? 'opacity-100' : 'opacity-0'}`}>
          <img src={s.image} alt="" className={`w-full h-full object-cover ${idx === i ? 'animate-slow-zoom' : ''}`} />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal/80 via-brand-charcoal/45 to-transparent" />
        </div>
      ))}
      <div className="relative h-full max-w-7xl mx-auto px-6 flex flex-col justify-center">
        <div key={i} className="max-w-xl animate-fade-up">
          <span className="inline-block text-brand-sand text-xs font-semibold uppercase tracking-[0.3em] mb-5">{slide.kicker}</span>
          <h1 className="font-display text-5xl md:text-7xl font-semibold text-brand-ivory leading-[1.02] text-balance">{slide.title}</h1>
          <p className="mt-6 text-lg text-brand-ivory/85 leading-relaxed max-w-md">{slide.subtitle}</p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Button asChild className="bg-brand-clay hover:bg-brand-clayDark text-white rounded-full px-8 h-14 text-base font-medium shadow-lg">
              <Link to={slide.cta.to}>{slide.cta.label} <ArrowRight size={18} className="ml-1" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full px-8 h-14 text-base font-medium bg-white/10 backdrop-blur border-white/40 text-white hover:bg-white hover:text-brand-charcoal">
              <Link to="/estimate">Free Estimate</Link>
            </Button>
          </div>
        </div>
      </div>
      {/* controls */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-5 z-10">
        <button onClick={prev} className="w-10 h-10 rounded-full border border-white/40 text-white flex items-center justify-center hover:bg-white hover:text-brand-charcoal transition-colors" aria-label="Previous"><ChevronLeft size={18} /></button>
        <div className="flex gap-2">
          {heroSlides.map((_, idx) => (
            <button key={idx} onClick={() => setI(idx)} className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-8 bg-brand-clay' : 'w-4 bg-white/50'}`} aria-label={`Slide ${idx + 1}`} />
          ))}
        </div>
        <button onClick={next} className="w-10 h-10 rounded-full border border-white/40 text-white flex items-center justify-center hover:bg-white hover:text-brand-charcoal transition-colors" aria-label="Next"><ChevronRight size={18} /></button>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = ['Free In-Home Samples', 'Honest, All-In Pricing', 'Experienced Installers', 'Free Measurements'];
  return (
    <div className="bg-brand-charcoal">
      <div className="max-w-7xl mx-auto px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
        {items.map((t) => (
          <div key={t} className="text-brand-ivory text-sm font-medium tracking-wide flex items-center justify-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-clay" />{t}
          </div>
        ))}
      </div>
    </div>
  );
}

function Categories() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-20 md:py-28">
      <Reveal>
        <SectionHeading eyebrow="Here to wow you" title="Explore our flooring" sub="Hundreds of premium name-brand styles — all viewable in your own home, in your own light." />
      </Reveal>
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {categories.map((c, idx) => (
          <Reveal key={c.slug} delay={idx * 80}>
            <Link to={`/flooring/${c.slug}`} className="cat-card group block relative rounded-2xl overflow-hidden h-80 shadow-sm hover:shadow-xl transition-shadow">
              <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/85 via-brand-charcoal/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <h3 className="font-display text-2xl font-semibold text-brand-ivory">{c.name}</h3>
                <p className="text-brand-ivory/75 text-sm mt-1.5 leading-snug max-h-0 opacity-0 group-hover:max-h-24 group-hover:opacity-100 transition-all duration-500 overflow-hidden">{c.short}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-brand-clay text-sm font-semibold">
                  Explore {c.name.split(' ')[0]} <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="bg-brand-cream">
      <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">
        <Reveal><SectionHeading center eyebrow="How it works" title="The showroom comes to you" sub="Skip the trip. We bring the flooring experience home in four simple steps." /></Reveal>
        <div className="mt-16 grid gap-8 md:grid-cols-4">
          {steps.map((s, idx) => {
            const Icon = stepIcons[idx];
            return (
              <Reveal key={s.id} delay={idx * 90}>
                <div className="relative text-center px-2">
                  <div className="mx-auto w-16 h-16 rounded-2xl bg-white shadow-sm border border-brand-line flex items-center justify-center text-brand-clay">
                    <Icon size={26} />
                  </div>
                  <span className="absolute -top-2 right-1/2 translate-x-10 text-5xl font-display font-semibold text-brand-sand/60 select-none">{s.id}</span>
                  <h3 className="mt-5 font-display text-xl font-semibold text-brand-charcoal">{s.title}</h3>
                  <p className="mt-2 text-brand-stone text-[15px] leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function AboutStrip() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="relative">
          <img src="https://images.unsplash.com/photo-1714976694531-2288f2708f77?auto=format&fit=crop&w=1200&q=80" alt="Flooring consultant with a homeowner" className="rounded-2xl w-full h-[480px] object-cover shadow-lg" />
          <div className="absolute -bottom-6 -right-4 md:right-6 bg-white rounded-2xl shadow-xl px-7 py-5 border border-brand-line">
            <p className="font-display text-4xl font-semibold text-brand-clay">15+</p>
            <p className="text-sm text-brand-stone">Years serving local homes</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={120}>
        <span className="text-brand-clay text-xs font-semibold uppercase tracking-[0.25em]">Your local flooring experts</span>
        <h2 className="font-display text-3xl md:text-[42px] font-semibold text-brand-charcoal mt-3 leading-tight text-balance">A better way to choose your floors</h2>
        <p className="mt-5 text-brand-stone text-[17px] leading-relaxed">
          Since {business.since}, Montifloors has helped homeowners across {business.serviceArea} find the perfect floor without the pressure of a traditional showroom. We bring hundreds of samples right to your living room, so every color and texture is judged in the light it will actually live in.
        </p>
        <p className="mt-4 text-brand-stone text-[17px] leading-relaxed">
          From your first sample to the final installed board, you work with one dedicated expert. That means honest advice, transparent pricing, and quality you can rely on.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Button asChild className="bg-brand-clay hover:bg-brand-clayDark text-white rounded-full px-7 h-13 py-3.5 font-medium">
            <Link to="/about">Learn More About Us</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full px-7 h-13 py-3.5 border-brand-charcoal/20 text-brand-charcoal hover:bg-brand-charcoal hover:text-brand-ivory font-medium">
            <a href={business.emailHref}>Email Us</a>
          </Button>
        </div>
      </Reveal>
    </section>
  );
}

function Commercial() {
  return (
    <section className="bg-brand-charcoal">
      <div className="max-w-7xl mx-auto px-6 py-20 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <span className="text-brand-clay text-xs font-semibold uppercase tracking-[0.25em]">For Business</span>
          <h2 className="font-display text-3xl md:text-[40px] font-semibold text-brand-ivory mt-3 leading-tight">Commercial flooring, on your schedule</h2>
          <p className="mt-5 text-brand-sand/80 text-[17px] leading-relaxed">Offices, retail, and hospitality spaces trust Montifloors for durable, design-forward flooring installed with minimal disruption to your day-to-day. We work after hours and weekends to keep your business moving.</p>
          <Button asChild className="mt-8 bg-brand-clay hover:bg-brand-clayDark text-white rounded-full px-7 h-13 py-3.5 font-medium">
            <Link to="/contact">Discuss Your Project</Link>
          </Button>
        </Reveal>
        <Reveal delay={120}>
          <img src="https://images.unsplash.com/photo-1746021375306-9dec0f637732?auto=format&fit=crop&w=1200&q=80" alt="Modern commercial office flooring" className="rounded-2xl w-full h-[420px] object-cover shadow-xl" />
        </Reveal>
      </div>
    </section>
  );
}

function ReviewsPreview() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-20 md:py-28">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <SectionHeading eyebrow="Customer reviews" title="Loved by local homeowners" sub="Don’t just take our word for it." />
          <div className="flex items-center gap-3 shrink-0">
            <span className="font-display text-5xl font-semibold text-brand-clay">4.9</span>
            <div>
              <div className="flex text-brand-clay">{[...Array(5)].map((_, k) => <Star key={k} size={16} fill="currentColor" />)}</div>
              <p className="text-sm text-brand-stone mt-1">200+ verified reviews</p>
            </div>
          </div>
        </div>
      </Reveal>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {reviews.slice(0, 3).map((r, idx) => (
          <Reveal key={r.name} delay={idx * 90}>
            <div className="bg-white rounded-2xl p-7 border border-brand-line h-full shadow-sm">
              <div className="flex text-brand-clay mb-4">{[...Array(r.rating)].map((_, k) => <Star key={k} size={16} fill="currentColor" />)}</div>
              <p className="text-brand-charcoal/85 leading-relaxed text-[15px]">“{r.text}”</p>
              <p className="mt-5 font-semibold text-brand-charcoal">{r.name}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="mt-10 text-center">
        <Button asChild variant="outline" className="rounded-full px-8 h-13 py-3.5 border-brand-charcoal/20 text-brand-charcoal hover:bg-brand-charcoal hover:text-brand-ivory font-medium">
          <Link to="/reviews">Read All Reviews</Link>
        </Button>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <TrustBar />
      <Categories />
      <HowItWorks />
      <AboutStrip />
      <HomeServicesBar />
      <EstimateBanner />
      <Commercial />
      <ReviewsPreview />
    </>
  );
}
